import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #2c3e50;
  border-radius: 8px;
`;

const Title = styled.h3`
  margin: 0;
  color: #ecf0f1;
  font-size: 16px;
`;

const StatusBadge = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => 
    props.type === 'resolved' ? '#2ecc71' :
    props.type === 'pending' ? '#f39c12' : '#e74c3c'
  };
  color: white;
`;

const DiscrepancyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 300px;
  overflow-y: auto;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(79, 172, 254, 0.5);
    }
  }
`;

const DiscrepancyItem = styled.div`
  background: #34495e;
  border-radius: 8px;
  padding: 16px;
  border-left: 4px solid ${props => 
    props.severity === 'high' ? '#e74c3c' :
    props.severity === 'medium' ? '#f39c12' : '#3498db'
  };
`;

const DiscrepancyHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const DiscrepancyType = styled.span`
  color: #ecf0f1;
  font-weight: 500;
  font-size: 14px;
`;

const DiscrepancyTime = styled.span`
  color: #95a5a6;
  font-size: 12px;
`;

const DiscrepancyDetails = styled.div`
  color: #bdc3c7;
  font-size: 13px;
  line-height: 1.4;
  margin-bottom: 8px;
`;

const DiscrepancyFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SeverityBadge = styled.span`
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  background: ${props => 
    props.severity === 'high' ? '#e74c3c' :
    props.severity === 'medium' ? '#f39c12' : '#3498db'
  };
  color: white;
`;

const ActionButton = styled.button`
  padding: 4px 8px;
  border: none;
  border-radius: 4px;
  background: #3498db;
  color: white;
  font-size: 11px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #2980b9;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #95a5a6;
  font-size: 14px;
`;

const DiscrepancyTracker = () => {
  const [discrepancies, setDiscrepancies] = useState([
    {
      id: 1,
      type: 'Inventory Mismatch',
      description: 'Physical inventory count differs from digital records for GOLD-001',
      severity: 'high',
      timestamp: new Date(Date.now() - 1800000), // 30 minutes ago
      status: 'pending'
    },
    {
      id: 2,
      type: 'Token Balance Discrepancy',
      description: 'Blockchain token balance does not match internal ledger',
      severity: 'medium',
      timestamp: new Date(Date.now() - 3600000), // 1 hour ago
      status: 'investigating'
    },
    {
      id: 3,
      type: 'Quality Verification',
      description: 'Quality score variance detected for SILVER-003',
      severity: 'low',
      timestamp: new Date(Date.now() - 7200000), // 2 hours ago
      status: 'resolved'
    }
  ]);

  const [summary, setSummary] = useState({
    total: 0,
    pending: 0,
    resolved: 0,
    high: 0,
    medium: 0,
    low: 0
  });

  useEffect(() => {
    const newSummary = discrepancies.reduce((acc, item) => {
      acc.total += 1;
      acc[item.status] = (acc[item.status] || 0) + 1;
      acc[item.severity] += 1;
      return acc;
    }, { total: 0, pending: 0, resolved: 0, investigating: 0, high: 0, medium: 0, low: 0 });

    setSummary(newSummary);
  }, [discrepancies]);

  const handleResolve = (id) => {
    setDiscrepancies(prev => 
      prev.map(item => 
        item.id === id ? { ...item, status: 'resolved' } : item
      )
    );
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ago`;
    }
    return `${minutes}m ago`;
  };

  const pendingDiscrepancies = discrepancies.filter(d => d.status !== 'resolved');

  return (
    <Container>
      <Header>
        <Title>Discrepancy Tracker</Title>
        <div style={{ display: 'flex', gap: '8px' }}>
          <StatusBadge type="critical">{summary.high} High</StatusBadge>
          <StatusBadge type="pending">{summary.medium} Medium</StatusBadge>
          <StatusBadge type="resolved">{summary.low} Low</StatusBadge>
        </div>
      </Header>

      <DiscrepancyList>
        {pendingDiscrepancies.length === 0 ? (
          <EmptyState>
            No active discrepancies found
          </EmptyState>
        ) : (
          pendingDiscrepancies.map(discrepancy => (
            <DiscrepancyItem key={discrepancy.id} severity={discrepancy.severity}>
              <DiscrepancyHeader>
                <DiscrepancyType>{discrepancy.type}</DiscrepancyType>
                <DiscrepancyTime>{formatTime(discrepancy.timestamp)}</DiscrepancyTime>
              </DiscrepancyHeader>
              
              <DiscrepancyDetails>
                {discrepancy.description}
              </DiscrepancyDetails>
              
              <DiscrepancyFooter>
                <SeverityBadge severity={discrepancy.severity}>
                  {discrepancy.severity}
                </SeverityBadge>
                {discrepancy.status === 'pending' && (
                  <ActionButton onClick={() => handleResolve(discrepancy.id)}>
                    Mark Resolved
                  </ActionButton>
                )}
              </DiscrepancyFooter>
            </DiscrepancyItem>
          ))
        )}
      </DiscrepancyList>
    </Container>
  );
};

export default DiscrepancyTracker; 